import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { THEME_KEYS } from "@/os/appearance";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const THEMES = Array.from(new Set([...THEME_KEYS, "theme-paper", "theme-navy", "theme-dark"]));

export function AppProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem("navitra-theme") || "theme-paper");
  const [density, setDensity] = useState(() => localStorage.getItem("navitra-density") || "compact");
  const [reduceMotion, setReduceMotion] = useState(() => localStorage.getItem("navitra-rm") === "true");
  const [osLayout, setOsLayout] = useState(() => localStorage.getItem("navitra-oslayout") || "navitra");
  const [osWallpaper, setOsWallpaper] = useState(() => localStorage.getItem("navitra-wallpaper") || "brand");
  const [lang, setLang] = useState(() => localStorage.getItem("navitra-lang") || "en");
  const [clockMode, setClockMode] = useState(() => localStorage.getItem("navitra-clock") || "digital");
  const [role, setRole] = useState(() => {
    const saved = localStorage.getItem("navitra-role");
    return saved ? JSON.parse(saved) : { key: "platform_owner", name: "Platform Owner", band: "admin" };
  });
  const [scopeDept, setScopeDept] = useState("all");
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("navitra-user");
    return saved ? JSON.parse(saved) : null;
  });

  const login = useCallback((u) => {
    setUser(u);
    localStorage.setItem("navitra-user", JSON.stringify(u));
    const r = { key: u.role_key, name: u.role, band: u.role_key === "employee" ? "user" : "admin" };
    setRole(r);
    localStorage.setItem("navitra-role", JSON.stringify(r));
  }, []);
  const logout = useCallback(() => {
    setUser(null);
    localStorage.removeItem("navitra-user");
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    THEMES.forEach((t) => html.classList.remove(t));
    html.classList.add(theme);
    localStorage.setItem("navitra-theme", theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.classList.toggle("density-compact", density === "compact");
    localStorage.setItem("navitra-density", density);
  }, [density]);

  useEffect(() => {
    document.documentElement.setAttribute("data-reduce-motion", String(reduceMotion));
    localStorage.setItem("navitra-rm", String(reduceMotion));
  }, [reduceMotion]);

  useEffect(() => {
    localStorage.setItem("navitra-oslayout", osLayout);
    document.documentElement.setAttribute("data-os-layout", osLayout);
  }, [osLayout]);

  useEffect(() => { localStorage.setItem("navitra-wallpaper", osWallpaper); }, [osWallpaper]);

  useEffect(() => { localStorage.setItem("navitra-clock", clockMode); }, [clockMode]);

  useEffect(() => {
    localStorage.setItem("navitra-lang", lang);
    document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const changeRole = useCallback((r) => {
    setRole(r);
    localStorage.setItem("navitra-role", JSON.stringify(r));
  }, []);

  return (
    <AppContext.Provider value={{ theme, setTheme, density, setDensity, reduceMotion, setReduceMotion, osLayout, setOsLayout, osWallpaper, setOsWallpaper, lang, setLang, clockMode, setClockMode, role, changeRole, scopeDept, setScopeDept, user, login, logout }}>
      {children}
    </AppContext.Provider>
  );
}
